import { FC, useMemo } from 'react';
import { X } from 'lucide-react';

import type { CourseOption } from '@teachur/http-client';
import Modal from '@new/common/ui/Modal/Modal';
import { SearchInput } from '@new/common/ui/SearchInput/SearchInput';
import { Loader } from '@new/common/ui/Loader/Loader';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';

import { useCoursePicker } from '../../hooks/useCoursePicker';
import { CourseBuilderList } from './CourseBuilderList';
import { CourseBuilderFooter } from './CourseBuilderFooter';

const OPTIONS_ERROR_MESSAGE = 'We couldn’t load the courses. Close this window and try again.';

interface AddCourseBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  groupLabel: string;
  categoryId?: number;
  excludedCourseIds: number[];
  multiple?: boolean;
  remainingCredits?: number | null;
  onConfirm: (courses: CourseOption[]) => void;
}

export const AddCourseBuilderModal: FC<AddCourseBuilderModalProps> = ({
  isOpen,
  onClose,
  title = 'Add Course',
  groupLabel,
  categoryId,
  excludedCourseIds,
  multiple = true,
  remainingCredits = null,
  onConfirm,
}) => {
  const picker = useCoursePicker(categoryId, isOpen);
  const excludedIds = useMemo(() => new Set(excludedCourseIds), [excludedCourseIds]);

  const close = () => {
    picker.reset();
    onClose();
  };

  const confirm = (courses: CourseOption[]) => {
    onConfirm(courses);
    close();
  };

  const selectedCredits = picker.selected.reduce((sum, course) => sum + course.credits, 0);
  const headroom = remainingCredits == null ? Infinity : remainingCredits - selectedCredits;

  const onToggle = (courseId: number) => {
    const course = picker.filtered.find((c) => c.courseId === courseId);
    if (!course) {
      return;
    }
    if (!multiple) {
      confirm([course]);
      return;
    }
    picker.toggle(course);
  };

  const onAdd = () => confirm(picker.selected);

  const subtitle =
    remainingCredits == null
      ? groupLabel
      : `${groupLabel} · ${remainingCredits} ${remainingCredits === 1 ? 'credit' : 'credits'} remaining`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      className="rounded-3xl! max-w-sm lg:max-w-3xl max-h-[min(42.875rem,calc(100dvh-2rem))]! md:h-175 lg:h-205.5"
      contentClassName="flex min-h-0 flex-1 flex-col gap-4"
    >
      <div className="flex shrink-0 flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h3 className="m-0! text-(--neutral-black-NB1000)! [font-size:var(--fs-h5)]! font-bold! lg:[font-size:var(--fs-h4)]!">
              {title}
            </h3>
            <p className="m-0! text-(--neutral-black-NB600)! [font-size:var(--fs-b4)]!">
              {subtitle}
            </p>
          </div>
          <button onClick={close} aria-label="Close modal" className="shrink-0 rounded-lg p-2">
            <X className="h-5 w-5 cursor-pointer text-(--neutral-black-NB600) lg:h-6 lg:w-6" />
          </button>
        </div>

        <SearchInput
          value={picker.search}
          onChange={picker.setSearch}
          placeholder="Search courses..."
          fullWidth
        />
      </div>

      <hr className="m-0! shrink-0 border-(--neutral-black-NB100)" />

      {picker.isLoading ? (
        <Loader />
      ) : picker.isError ? (
        <EmptyState message={OPTIONS_ERROR_MESSAGE} />
      ) : (
        <CourseBuilderList
          courses={picker.filtered}
          selectedIds={picker.selectedIds}
          excludedIds={excludedIds}
          headroom={headroom}
          countsTowardLabel={groupLabel}
          selectable={multiple}
          onToggle={onToggle}
        />
      )}

      {multiple ? (
        <CourseBuilderFooter
          selectedCount={picker.selected.length}
          selectedCredits={selectedCredits}
          onCancel={close}
          onAdd={onAdd}
        />
      ) : null}
    </Modal>
  );
};
